import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { centralizedLoginCheck, markUserLanding, getGlid, getAk } from '../../utils';
import { StatutoryDetailsProvider } from "../../contexts/StatutoryDetailsContext";
import { useStatutoryDetails } from "../../contexts/StatutoryDetailsContext";
import { ConsentProvider } from "../../contexts/ConsentContext";
import { useConsent } from "../../contexts/ConsentContext";
import { UserJourneyProvider } from "../../contexts/UserJourneyContext";
import Layout from "../Common/Layout";
import { useLoginModal } from "../Common/LoginModalProvider";
import ProtectedRoute from "../Common/ProtectedRoute";
import GoldLoanLandingPage from "../Pages/GoldLoanLandingPage";

// Paths where consent modal should appear
const CONSENT_INCLUDED_PATHS = ['/businessloan'];
// import SentryTest from "../SentryTest";

// Lazy-loaded page components
const HelloTradePage = lazy(() => import("../Pages/HelloTradePage"));
const BureauReportForm = lazy(() => import("../HelloTrade/BureauReport"));
const BenefitsComponent = lazy(() => import("../BureauJourney/Components/BenefitsComponent"));
const AppTest = lazy(() => import("../Pages/AppTest"));
const TncSComponent = lazy(() => import("../Pages/TnCsComponent"));
const PrivacyPolicyComponent = lazy(() => import("../Pages/PrivacyPolicyComponent"));
const LenderPartnerComponent = lazy(() => import("../Pages/LenderPartnerComponent").then(m => ({ default: m.LenderPartnerComponent })));
const FixerraPage = lazy(() => import("../Pages/FixerraPage").then(m => ({ default: m.FixerraPage })));
const PreApprovedLoanOffer = lazy(() => import("../Pages/PreApprovedLoanOffer"));
const HelloTradeLandingPage = lazy(() => import("../Pages/HelloTradeLandingPage"));
const CreditCardPage = lazy(() => import("../Pages/CreditCardPage"));
const InvestorRelationsPage = lazy(() => import("../Pages/InvestorRelationsPage"));
const PersonalLoanLandingPage = lazy(() => import("../Pages/PersonalLoanLandingPage"));
const PersonalLoanIneligiblePage = lazy(() => import("../Pages/PersonalLoanIneligiblePage"));
const AccountAggregatorTestPage = lazy(() => import("../Pages/AccountAggregatorTestPage"));
const CameraPage = lazy(() => import("../Pages/CameraPage"));

// Loading fallback component
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      fontSize: '16px',
      color: '#666'
    }}>
      Loading...
    </div>
  );
}

// Custom wrapper to access location inside Routes
function RouterWithLoginCheck() {
  const location = useLocation();
  const { setStatutoryDetails } = useStatutoryDetails();
  const { closeLoginModal } = useLoginModal();
  const { checkUserConsent } = useConsent();

  useEffect(() => {
    if (location.pathname == "/credit-cards"){
      markUserLanding(location.pathname);
    }
  }, [location.pathname]);

  // Check consent on route change (for already-logged-in users)
  useEffect(() => {
    if (!CONSENT_INCLUDED_PATHS.includes(location.pathname)) return;
    const glid = getGlid();
    const ak = getAk();
    if (glid && ak) {
      checkUserConsent(glid, ak);
    }
  }, [location.pathname]); // checkUserConsent is stable (empty useCallback deps)

  // Check consent after login modal success (user was not logged in on page load)
  useEffect(() => {
    const handleLogin = ({ detail: { glid, ak } }) => {
      if (CONSENT_INCLUDED_PATHS.includes(location.pathname) && glid && ak) {
        checkUserConsent(glid, ak);
      }
    };
    window.addEventListener('ht:login', handleLogin);
    return () => window.removeEventListener('ht:login', handleLogin);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      //console.log("Waiting for Statutary Data in router")
      const formRes = await centralizedLoginCheck(location.pathname, location.search);

      // If centralizedLoginCheck fetched user data, store it in context
      if (formRes && formRes.CODE == 200) {
        setStatutoryDetails(formRes.USER_DATA);
      }

      //console.log("Unable to update statutory Data")

    })();
  }, [location.pathname, setStatutoryDetails]);

  // Close login modal when route changes
  useEffect(() => {
    closeLoginModal();
  }, [location.pathname, closeLoginModal]);

  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/businessloan" element={<HelloTradePage />} />
          <Route path="/downloadReport" element={<BureauReportForm />} />
          <Route path="benefit" element={<BenefitsComponent />} />
          <Route path="/appTest" element={<AppTest />} />
          <Route path="/camera" element={<CameraPage />} />
          <Route path="/terms-and-conditions" element={<TncSComponent />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyComponent />} />
          <Route path="/lending-partners" element={<LenderPartnerComponent />} />
          <Route path="/fixed-deposit" element={<FixerraPage />} />
          <Route path="/pre-approved-offer" element={<PreApprovedLoanOffer />} />
          <Route path="/credit-cards" element={<CreditCardPage />} />
          <Route path="/investor-relations" element={<InvestorRelationsPage />} />
          <Route path="/" element={<HelloTradeLandingPage />} />
          {/* <Route path="/aa-test" element={<AccountAggregatorTestPage />} /> */}
          <Route path="/personalloan" element={<PersonalLoanLandingPage />} />
          <Route
            path="/personalloan/ineligible"
            element={
              <ProtectedRoute
                element={<PersonalLoanIneligiblePage />}
                requireAuth={true}
                requireState={true}
                stateProp="allowed"
                fallbackPath="/personalloan"
              />
            }
          />
          {/* <Route path="/sentry-test" element={<SentryTest />} /> */}
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default function RouterComponent() {
  return (
    <Router>
      <StatutoryDetailsProvider>
        <UserJourneyProvider>
          <ConsentProvider>
            <RouterWithLoginCheck />
          </ConsentProvider>
        </UserJourneyProvider>
      </StatutoryDetailsProvider>
    </Router>
  );
}
